import { createContext, PropsWithChildren, useContext } from 'react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { getEnvVariables } from '@/env/getEnvVariables';
import { ApplicationInsightsEvent } from './application-insights-events';
import { createBrowserHistory } from 'history';
import { scrubOAuthParams } from '@/utils/scrub-oauth-params';

export type ApplicationInsightsContextType = {
  appInsights: ApplicationInsights;
};

export const ApplicationInsightsContext = createContext<ApplicationInsightsContextType>({
  // @ts-expect-error todo: fix type
  appInsights: undefined,
});

export const ApplicationInsightsProvider = ({ children }: PropsWithChildren) => {
  const reactPlugin = new ReactPlugin();
  const browserHistory = createBrowserHistory();

  const appInsights = new ApplicationInsights({
    config: {
      connectionString: getEnvVariables().applicationInsightsConnectionString,
      extensions: [reactPlugin],
      extensionConfig: {
        [reactPlugin.identifier]: { history: browserHistory },
      },
    },
  });

  // Registered before loadAppInsights so no page view can escape unscrubbed:
  // the /mobile-auth redirect carries an OAuth code in its query string.
  appInsights.addTelemetryInitializer((item) => {
    const uri = item.baseData?.uri;
    if (typeof uri === 'string') item.baseData!.uri = scrubOAuthParams(uri);

    const url = item.baseData?.url;
    if (typeof url === 'string') item.baseData!.url = scrubOAuthParams(url);

    if (typeof item.uri === 'string') item.uri = scrubOAuthParams(item.uri);
  });

  if (process.env.NODE_ENV !== 'development') {
    appInsights.loadAppInsights();
  }

  return (
    <ApplicationInsightsContext.Provider value={{ appInsights }}>
      {children}
    </ApplicationInsightsContext.Provider>
  );
};

export const useTrackAppInsightsEvent = () => {
  const { appInsights } = useContext(ApplicationInsightsContext);

  return (event: ApplicationInsightsEvent, properties?: Record<string, string>) =>
    appInsights.trackEvent({
      name: event,
      properties,
    });
};
